import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggerService } from './logger.service';

/**
 * Storage type enumeration
 */
export enum StorageType {
    INDEXEDDB = 'indexeddb',
    LOCALSTORAGE = 'localstorage',
    AUTO = 'auto' // Auto-detect and use best available
}

/**
 * Storage configuration options
 */
export interface StorageConfig {
    storageType?: StorageType;
    dbName?: string;
    dbVersion?: number;
    storeName?: string;
}

/**
 * Query options for filtering data
 */
export interface QueryOptions<T = unknown> {
    limit?: number;
    offset?: number;
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
    filter?: (item: T) => boolean;
}

/**
 * Storage adapter interface
 */
interface IStorageAdapter {
    get<T>(key: string): Promise<T | null>;
    set<T>(key: string, value: T): Promise<void>;
    delete(key: string): Promise<void>;
    clear(): Promise<void>;
    keys(): Promise<string[]>;
    has(key: string): Promise<boolean>;
    query<T>(options?: QueryOptions): Promise<T[]>;
}

/**
 * LocalStorage Adapter
 */
class LocalStorageAdapter implements IStorageAdapter {
    constructor(private logger: LoggerService) { }

    async get<T>(key: string): Promise<T | null> {
        try {
            const item = localStorage.getItem(key);
            if (item === null) return null;

            const parsed = JSON.parse(item);
            return parsed.value as T;
        } catch (error) {
            this.logger.error(`Error getting item from localStorage: ${key}`, error);
            return null;
        }
    }

    async set<T>(key: string, value: T): Promise<void> {
        try {
            const item = {
                value,
                timestamp: new Date().toISOString(),
                version: 1
            };
            localStorage.setItem(key, JSON.stringify(item));
        } catch (error) {
            this.logger.error(`Error setting item in localStorage: ${key}`, error);
            throw error;
        }
    }

    async delete(key: string): Promise<void> {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            this.logger.error(`Error deleting item from localStorage: ${key}`, error);
            throw error;
        }
    }

    async clear(): Promise<void> {
        try {
            localStorage.clear();
        } catch (error) {
            this.logger.error('Error clearing localStorage', error);
            throw error;
        }
    }

    async keys(): Promise<string[]> {
        try {
            return Object.keys(localStorage);
        } catch (error) {
            this.logger.error('Error getting localStorage keys', error);
            return [];
        }
    }

    async has(key: string): Promise<boolean> {
        return localStorage.getItem(key) !== null;
    }

    async query<T>(options?: QueryOptions): Promise<T[]> {
        try {
            const allKeys = await this.keys();
            let results: T[] = [];

            for (const key of allKeys) {
                const value = await this.get<T>(key);
                if (value !== null) {
                    results.push(value);
                }
            }

            // Apply filter
            if (options?.filter) {
                results = results.filter(options.filter);
            }

            // Apply ordering
            if (options?.orderBy) {
                const direction = options.orderDirection === 'desc' ? -1 : 1;
                results.sort((a: T, b: T) => {
                    const aVal = (a as any)[options.orderBy!];
                    const bVal = (b as any)[options.orderBy!];
                    return aVal > bVal ? direction : aVal < bVal ? -direction : 0;
                });
            }

            // Apply pagination
            if (options?.offset !== undefined || options?.limit !== undefined) {
                const start = options.offset || 0;
                const end = options.limit ? start + options.limit : undefined;
                results = results.slice(start, end);
            }

            return results;
        } catch (error) {
            this.logger.error('Error querying localStorage', error);
            return [];
        }
    }
}

/**
 * IndexedDB Adapter
 */
class IndexedDBAdapter implements IStorageAdapter {
    private db: IDBDatabase | null = null;
    private readonly DB_NAME: string;
    private readonly DB_VERSION: number;
    private readonly STORE_NAME: string;

    constructor(
        private logger: LoggerService,
        dbName: string = 'AppStorage',
        dbVersion: number = 1,
        storeName: string = 'keyvalue'
    ) {
        this.DB_NAME = dbName;
        this.DB_VERSION = dbVersion;
        this.STORE_NAME = storeName;
    }

    private async openDB(): Promise<IDBDatabase> {
        if (this.db) return this.db;

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

            request.onerror = () => {
                this.logger.error('Error opening IndexedDB', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(request.result);
            };

            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = (event.target as IDBOpenDBRequest).result as IDBDatabase;

                // Create object store if it doesn't exist
                if (!db.objectStoreNames.contains(this.STORE_NAME)) {
                    const objectStore = db.createObjectStore(this.STORE_NAME, { keyPath: 'key' });
                    objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                }
            };
        });
    }

    async get<T>(key: string): Promise<T | null> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.STORE_NAME], 'readonly');
                const store = transaction.objectStore(this.STORE_NAME);
                const request = store.get(key);

                request.onsuccess = () => {
                    const result = request.result;
                    resolve(result ? result.value : null);
                };

                request.onerror = () => {
                    this.logger.error(`Error getting item from IndexedDB: ${key}`, request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            this.logger.error(`Error in IndexedDB get operation: ${key}`, error);
            return null;
        }
    }

    async set<T>(key: string, value: T): Promise<void> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.STORE_NAME], 'readwrite');
                const store = transaction.objectStore(this.STORE_NAME);

                const item = {
                    key,
                    value,
                    timestamp: new Date().toISOString(),
                    version: 1
                };

                const request = store.put(item);

                request.onsuccess = () => resolve();
                request.onerror = () => {
                    this.logger.error(`Error setting item in IndexedDB: ${key}`, request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            this.logger.error(`Error in IndexedDB set operation: ${key}`, error);
            throw error;
        }
    }

    async delete(key: string): Promise<void> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.STORE_NAME], 'readwrite');
                const store = transaction.objectStore(this.STORE_NAME);
                const request = store.delete(key);

                request.onsuccess = () => resolve();
                request.onerror = () => {
                    this.logger.error(`Error deleting item from IndexedDB: ${key}`, request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            this.logger.error(`Error in IndexedDB delete operation: ${key}`, error);
            throw error;
        }
    }

    async clear(): Promise<void> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.STORE_NAME], 'readwrite');
                const store = transaction.objectStore(this.STORE_NAME);
                const request = store.clear();

                request.onsuccess = () => resolve();
                request.onerror = () => {
                    this.logger.error('Error clearing IndexedDB', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            this.logger.error('Error in IndexedDB clear operation', error);
            throw error;
        }
    }

    async keys(): Promise<string[]> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.STORE_NAME], 'readonly');
                const store = transaction.objectStore(this.STORE_NAME);
                const request = store.getAllKeys();

                request.onsuccess = () => resolve(request.result as string[]);
                request.onerror = () => {
                    this.logger.error('Error getting IndexedDB keys', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            this.logger.error('Error in IndexedDB keys operation', error);
            return [];
        }
    }

    async has(key: string): Promise<boolean> {
        const value = await this.get(key);
        return value !== null;
    }

    async query<T>(options?: QueryOptions): Promise<T[]> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.STORE_NAME], 'readonly');
                const store = transaction.objectStore(this.STORE_NAME);
                const request = store.getAll();

                request.onsuccess = () => {
                    let results: T[] = request.result.map((item: { key: string; value: T }) => item.value);

                    // Apply filter
                    if (options?.filter) {
                        results = results.filter(options.filter);
                    }

                    // Apply ordering
                    if (options?.orderBy) {
                        const direction = options.orderDirection === 'desc' ? -1 : 1;
                        results.sort((a: T, b: T) => {
                            const aVal = (a as any)[options.orderBy!];
                            const bVal = (b as any)[options.orderBy!];
                            return aVal > bVal ? direction : aVal < bVal ? -direction : 0;
                        });
                    }

                    // Apply pagination
                    if (options?.offset !== undefined || options?.limit !== undefined) {
                        const start = options.offset || 0;
                        const end = options.limit ? start + options.limit : undefined;
                        results = results.slice(start, end);
                    }

                    resolve(results);
                };

                request.onerror = () => {
                    this.logger.error('Error querying IndexedDB', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            this.logger.error('Error in IndexedDB query operation', error);
            return [];
        }
    }
}

/**
 * Main Storage Service
 */
@Injectable({
    providedIn: 'root'
})
export class StorageService {
    private logger = inject(LoggerService);
    private adapter!: IStorageAdapter;
    private currentStorageType!: StorageType;

    constructor() {
        // Initialize with auto-detection by default
        this.initialize({ storageType: StorageType.AUTO });
    }

    /**
     * Initialize storage with configuration
     */
    initialize(config: StorageConfig = {}): void {
        const {
            storageType = StorageType.AUTO,
            dbName = 'AppStorage',
            dbVersion = 1,
            storeName = 'keyvalue'
        } = config;

        let selectedType = storageType;

        // Auto-detect if requested
        if (storageType === StorageType.AUTO) {
            selectedType = this.detectBestStorage();
            this.logger.log(`Auto-detected storage type: ${selectedType}`);
        }

        // Initialize appropriate adapter
        if (selectedType === StorageType.INDEXEDDB && this.isIndexedDBAvailable()) {
            this.adapter = new IndexedDBAdapter(this.logger, dbName, dbVersion, storeName);
            this.currentStorageType = StorageType.INDEXEDDB;
            this.logger.log('Storage service initialized with IndexedDB');
        } else {
            this.adapter = new LocalStorageAdapter(this.logger);
            this.currentStorageType = StorageType.LOCALSTORAGE;
            this.logger.log('Storage service initialized with localStorage');
        }
    }

    /**
     * Get current storage type
     */
    getStorageType(): StorageType {
        return this.currentStorageType;
    }

    /**
     * Check if IndexedDB is available
     */
    private isIndexedDBAvailable(): boolean {
        try {
            return typeof indexedDB !== 'undefined' && indexedDB !== null;
        } catch {
            return false;
        }
    }

    /**
     * Detect the best available storage
     */
    private detectBestStorage(): StorageType {
        return this.isIndexedDBAvailable() ? StorageType.INDEXEDDB : StorageType.LOCALSTORAGE;
    }

    /**
     * Get item from storage
     */
    async get<T>(key: string): Promise<T | null> {
        return this.adapter.get<T>(key);
    }

    /**
     * Get item as Observable
     */
    get$<T>(key: string): Observable<T | null> {
        return from(this.adapter.get<T>(key)).pipe(
            catchError(error => {
                this.logger.error(`Error in get$ operation for key: ${key}`, error);
                return of(null);
            })
        );
    }

    /**
     * Set item in storage
     */
    async set<T>(key: string, value: T): Promise<void> {
        return this.adapter.set(key, value);
    }

    /**
     * Set item as Observable
     */
    set$<T>(key: string, value: T): Observable<void> {
        return from(this.adapter.set(key, value)).pipe(
            catchError(error => {
                this.logger.error(`Error in set$ operation for key: ${key}`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Delete item from storage
     */
    async delete(key: string): Promise<void> {
        return this.adapter.delete(key);
    }

    /**
     * Delete item as Observable
     */
    delete$(key: string): Observable<void> {
        return from(this.adapter.delete(key)).pipe(
            catchError(error => {
                this.logger.error(`Error in delete$ operation for key: ${key}`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Clear all items from storage
     */
    async clear(): Promise<void> {
        return this.adapter.clear();
    }

    /**
     * Clear as Observable
     */
    clear$(): Observable<void> {
        return from(this.adapter.clear()).pipe(
            catchError(error => {
                this.logger.error('Error in clear$ operation', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Get all keys from storage
     */
    async keys(): Promise<string[]> {
        return this.adapter.keys();
    }

    /**
     * Get keys as Observable
     */
    keys$(): Observable<string[]> {
        return from(this.adapter.keys()).pipe(
            catchError(error => {
                this.logger.error('Error in keys$ operation', error);
                return of([]);
            })
        );
    }

    /**
     * Check if key exists
     */
    async has(key: string): Promise<boolean> {
        return this.adapter.has(key);
    }

    /**
     * Check if key exists as Observable
     */
    has$(key: string): Observable<boolean> {
        return from(this.adapter.has(key)).pipe(
            catchError(error => {
                this.logger.error(`Error in has$ operation for key: ${key}`, error);
                return of(false);
            })
        );
    }

    /**
     * Query data with filters and pagination
     */
    async query<T>(options?: QueryOptions): Promise<T[]> {
        return this.adapter.query<T>(options);
    }

    /**
     * Query as Observable
     */
    query$<T>(options?: QueryOptions): Observable<T[]> {
        return from(this.adapter.query<T>(options)).pipe(
            catchError(error => {
                this.logger.error('Error in query$ operation', error);
                return of([]);
            })
        );
    }

    /**
     * Get multiple items by keys
     */
    async getMultiple<T>(keys: string[]): Promise<Map<string, T | null>> {
        const results = new Map<string, T | null>();

        for (const key of keys) {
            const value = await this.get<T>(key);
            results.set(key, value);
        }

        return results;
    }

    /**
     * Set multiple items at once
     */
    async setMultiple<T>(items: Map<string, T>): Promise<void> {
        const promises: Promise<void>[] = [];

        items.forEach((value, key) => {
            promises.push(this.set(key, value));
        });

        await Promise.all(promises);
    }

    /**
     * Delete multiple items by keys
     */
    async deleteMultiple(keys: string[]): Promise<void> {
        const promises = keys.map(key => this.delete(key));
        await Promise.all(promises);
    }
}
